import {
  useRef,
  useState,
  type ComponentProps,
  type ComponentType,
} from "react";
import {
  Animated,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { GlassView, isGlassEffectAPIAvailable } from "expo-glass-effect";
import { ChevronRight } from "lucide-react-native";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { ChoiceGlassBackdrop } from "./OnboardingLayout";
import { semanticColors } from "@louise/theme/tokens";

type ChoiceIcon = ComponentType<
  Pick<ComponentProps<typeof ChevronRight>, "size" | "color" | "strokeWidth">
>;

type Props = {
  Icon?: ChoiceIcon;
  label: string;
  sublabel?: string;
  selected: boolean;
  compact: boolean;
  onPress: () => void;
  accessibilityLabel?: string;
  borderRadius?: number;
  backdropTint?: "dark" | "default";
  hoverBackgroundColor?: string;
  centered?: boolean;
  showChevron?: boolean;
  showBackdrop?: boolean;
};

/**
 * Shared outlined-glass treatment for the branching onboarding choices.
 * Keeping Goals and JourneyStage on one component prevents their resting,
 * hover, and selected states from drifting apart.
 */
export default function GlassChoiceButton({
  Icon,
  label,
  sublabel,
  selected,
  compact,
  onPress,
  accessibilityLabel,
  borderRadius = 18,
  backdropTint = "dark",
  hoverBackgroundColor = semanticColors.onDark.fill.subtle,
  centered = false,
  showChevron = true,
  showBackdrop = true,
}: Props) {
  const hasIOSGlassEffect =
    Platform.OS === "ios" && isGlassEffectAPIAvailable();
  const capturePress = usePressCapture("GlassChoiceButton");
  const scale = useRef(new Animated.Value(1)).current;
  const [hovered, setHovered] = useState(false);

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.97,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      friction: 4,
      useNativeDriver: true,
    }).start();
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? `${label} option`}
      accessibilityState={{ selected }}
      aria-selected={selected}
      onPress={() => {
        // The label arrives already translated, so the funnel keys off the
        // stable control identifier rather than the visible copy.
        capturePress(
          pressLabel(label, accessibilityLabel),
          "onboarding_glass_choice_button",
        );
        onPress();
      }}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => ({
        opacity: hasIOSGlassEffect ? 1 : pressed ? 0.94 : 1,
      })}
    >
      <Animated.View
        style={[
          {
            transform: [{ scale }],
            flexDirection: "row",
            alignItems: "center",
            justifyContent: centered ? "center" : "flex-start",
            gap: compact ? 14 : 18,
            paddingVertical: compact ? 13 : 18,
            paddingHorizontal: compact ? 16 : 20,
            overflow: "hidden",
            backgroundColor: selected
              ? semanticColors.onDark.fill.strong
              : hovered
                ? hoverBackgroundColor
                : "transparent",
            borderRadius,
            borderWidth: 1,
            borderColor: selected
              ? "rgba(255, 244, 236, 0.34)"
              : hovered
                ? "rgba(255, 244, 236, 0.24)"
                : "rgba(255, 239, 225, 0.15)",
            borderTopColor: selected
              ? "rgba(255, 250, 246, 0.46)"
              : hovered
                ? "rgba(255, 248, 242, 0.38)"
                : "rgba(255, 244, 236, 0.28)",
            borderBottomColor: "rgba(255, 225, 207, 0.10)",
          },
          Platform.OS === "web" && {
            transition: "background-color 140ms ease, border-color 140ms ease",
          },
        ]}
      >
        {hasIOSGlassEffect && (
          <GlassView
            pointerEvents="none"
            glassEffectStyle="clear"
            tintColor={
              selected ? "rgba(255, 244, 236, 0.14)" : "rgba(71, 28, 10, 0.10)"
            }
            colorScheme="dark"
            isInteractive
            style={[StyleSheet.absoluteFill, { borderRadius }]}
          />
        )}
        {!hasIOSGlassEffect && showBackdrop && (hovered || selected) && (
          <ChoiceGlassBackdrop
            borderRadius={borderRadius - 1}
            tint={backdropTint}
          />
        )}
        {Icon && (
          <View style={{ zIndex: 1 }}>
            <Icon
              size={compact ? 19 : 21}
              color={semanticColors.onDark.text.primary}
              strokeWidth={1.5}
            />
          </View>
        )}
        <View
          style={{
            flex: centered ? undefined : 1,
            zIndex: 1,
            alignItems: centered ? "center" : "flex-start",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: compact ? 14 : 15,
              fontWeight: "500",
              color: semanticColors.onDark.text.primary,
            }}
          >
            {label}
          </Text>
          {sublabel && (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: compact ? 11 : 12,
                lineHeight: compact ? 15 : 17,
                color: semanticColors.onDark.text.secondary,
                marginTop: 3,
              }}
            >
              {sublabel}
            </Text>
          )}
        </View>
        {showChevron && (
          <View style={{ zIndex: 1 }}>
            <ChevronRight
              size={compact ? 14 : 16}
              color={semanticColors.onDark.text.tertiary}
            />
          </View>
        )}
      </Animated.View>
    </Pressable>
  );
}
